import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Award, GraduationCap, TrendingDown, Users } from "lucide-react";
import {
  apiGetAnalyticsHeadcount,
  apiGetAnalyticsTurnover,
  apiGetAnalyticsDepartmentComparison,
  apiGetCertificationOverview,
  apiGetTrainingOverview,
} from "@/lib/api";
import { MobilePivotReportBuilder } from "@/components/analytics/MobilePivotReportBuilder";

const EXPIRY_WINDOW_DAYS = 30;

function countExpiringSoon(items: { expiryDate: string | null }[]): number {
  const now = Date.now();
  const horizon = now + EXPIRY_WINDOW_DAYS * 86400000;
  return items.filter((i) => {
    if (!i.expiryDate) return false;
    const t = new Date(i.expiryDate).getTime();
    return t >= now && t <= horizon;
  }).length;
}

function KpiCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | number }) {
  return (
    <div className="bg-card border border-border/60 rounded-2xl p-4 flex flex-col gap-2">
      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground leading-none">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </div>
  );
}

function BarRow({ label, value, max, color }: { label: string; value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-foreground/80 truncate">{label}</span>
        <span className="text-muted-foreground shrink-0 ml-2">{value}</span>
      </div>
      <div className="w-full bg-muted rounded-full h-1.5">
        <div className={`${color} rounded-full h-1.5`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function MobileHRAnalytics() {
  const { t } = useTranslation();

  const { data: headcount } = useQuery({
    queryKey: ["mobile-analytics-headcount"],
    queryFn: () => apiGetAnalyticsHeadcount(),
    staleTime: 5 * 60_000,
  });

  const { data: turnover } = useQuery({
    queryKey: ["mobile-analytics-turnover"],
    queryFn: () => apiGetAnalyticsTurnover(),
    staleTime: 5 * 60_000,
  });

  const { data: deptComparison } = useQuery({
    queryKey: ["mobile-analytics-department-comparison"],
    queryFn: apiGetAnalyticsDepartmentComparison,
    staleTime: 5 * 60_000,
  });

  const { data: certOverview } = useQuery({
    queryKey: ["mobile-analytics-certifications"],
    queryFn: apiGetCertificationOverview,
    staleTime: 5 * 60_000,
  });

  const { data: trainingOverview } = useQuery({
    queryKey: ["mobile-analytics-trainings"],
    queryFn: apiGetTrainingOverview,
    staleTime: 5 * 60_000,
  });

  const maxDeptCount = Math.max(1, ...(headcount?.byDepartment.map((d) => d.count) ?? [0]));

  const expiringCerts = useMemo(
    () => (certOverview ?? []).reduce((sum, e) => sum + countExpiringSoon(e.heldCerts), 0),
    [certOverview],
  );
  const expiringTrainings = useMemo(
    () => (trainingOverview ?? []).reduce((sum, e) => sum + countExpiringSoon(e.heldTrainings), 0),
    [trainingOverview],
  );

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3">
        <KpiCard icon={Users} label={t("mobile.hrAnalytics.totalHeadcount")} value={headcount?.total ?? "—"} />
        <KpiCard icon={Users} label={t("mobile.hrAnalytics.active")} value={headcount?.active ?? "—"} />
        <KpiCard icon={Users} label={t("mobile.hrAnalytics.onLeave")} value={headcount?.onLeave ?? "—"} />
        <KpiCard icon={Users} label={t("mobile.hrAnalytics.newHires")} value={headcount?.newHiresThisMonth ?? "—"} />
      </div>

      {/* Turnover */}
      {turnover && (
        <div className="bg-card border border-border/60 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-foreground">{t("mobile.hrAnalytics.turnover")}</h2>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-lg font-bold text-foreground">{turnover.totalHires}</p>
              <p className="text-[11px] text-muted-foreground">{t("mobile.hrAnalytics.hires")}</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{turnover.totalTerminations}</p>
              <p className="text-[11px] text-muted-foreground">{t("mobile.hrAnalytics.terminations")}</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{turnover.avgTurnoverRate.toFixed(1)}%</p>
              <p className="text-[11px] text-muted-foreground">{t("mobile.hrAnalytics.turnoverRate")}</p>
            </div>
          </div>
        </div>
      )}

      {/* Headcount by department */}
      {headcount && headcount.byDepartment.length > 0 && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 space-y-2.5">
          <h2 className="text-sm font-semibold text-foreground mb-1">{t("mobile.hrAnalytics.byDepartment")}</h2>
          {headcount.byDepartment.map((d) => (
            <BarRow key={d.department} label={d.department} value={d.count} max={maxDeptCount} color="bg-primary" />
          ))}
        </div>
      )}

      {/* Department avg tenure */}
      {deptComparison && deptComparison.departments.length > 0 && (
        <div className="bg-card border border-border/60 rounded-2xl p-4 space-y-2.5">
          <h2 className="text-sm font-semibold text-foreground mb-1">{t("mobile.hrAnalytics.avgTenure")}</h2>
          {deptComparison.departments.map((d) => (
            <div key={d.department} className="flex justify-between text-sm">
              <span className="text-foreground/80">{d.department}</span>
              <span className="text-muted-foreground">{d.avgTenureMonths.toFixed(1)} {t("mobile.hrAnalytics.months")}</span>
            </div>
          ))}
        </div>
      )}

      {/* Compliance */}
      <div className="bg-card border border-border/60 rounded-2xl p-4 space-y-3">
        <h2 className="text-sm font-semibold text-foreground">{t("mobile.hrAnalytics.compliance")}</h2>
        {certOverview && (
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
              <Award className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {expiringCerts} {t("mobile.hrAnalytics.expiringCertifications")}
              </p>
            </div>
          </div>
        )}
        {trainingOverview && (
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <GraduationCap className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {expiringTrainings} {t("mobile.hrAnalytics.expiringTrainings")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pivot report builder */}
      <MobilePivotReportBuilder />
    </div>
  );
}
